import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Context
import { ConfigurationProvider } from './context/ConfigurationContext';

// Feature flags
import { FEATURES } from './constants/featureFlags';

// Route protection
import { ProtectedRoute } from './components/ProtectedRoute';

// Pages
import LandingV6 from './pages/LandingV6';
import PrelaunchWaitlistPage from './components/landing/waitlist/PrelaunchWaitlistPage';
import { PRELAUNCH_MODE } from './config/prelaunch';
import PartnersPage from './pages/PartnersPage';
import CaregiverPortal from './CaregiverPortal';
import { Dashboard } from './pages';
import ManageSeniors from './pages/ManageSeniors';
import { ConfigurationWizard } from './pages/configuration';
import HelpSupport from './pages/HelpSupport';
import MyAccount from './pages/MyAccount';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import FaqClusterPage from './pages/faq/FaqClusterPage';
import DeleteAccount from './pages/DeleteAccount';
import NotFound from './pages/NotFound';
import { useHtmlLang } from './hooks/useHtmlLang';
import { RecoveryLinkCatcher } from './components/RecoveryLinkCatcher';

function App() {
  // Keeps <html lang> correct on the client-only routes (/dashboard, /account,
  // /help), which never set it and shipped with the attribute empty.
  useHtmlLang();

  return (
    <Router>
      <ConfigurationProvider>
        {/* D18 — forwards a Supabase recovery link that landed on the wrong
            route (misconfigured Auth redirect allowlist) to /reset-password,
            so a locked-out caregiver is never stranded on the landing page. */}
        <RecoveryLinkCatcher />
        <Routes>
          <Route path="/" element={<LandingV6 />} />
          <Route path="/fr" element={<LandingV6 />} />
          <Route path="/about" element={<About />} />
          <Route path="/fr/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/fr/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/fr/faq" element={<FAQ />} />
          {/* FAQ cluster batch 1 (Ticket 5) — locked content, prerendered */}
          <Route path="/faq/living-alone-and-far-away" element={<FaqClusterPage />} />
          <Route path="/fr/faq/parent-seul-a-distance" element={<FaqClusterPage />} />
          <Route path="/faq/price-trial-cancelling" element={<FaqClusterPage />} />
          <Route path="/fr/faq/prix-essai-annulation" element={<FaqClusterPage />} />
          <Route path="/faq/memory-vision-movement" element={<FaqClusterPage />} />
          <Route path="/fr/faq/memoire-vision-mobilite" element={<FaqClusterPage />} />
          <Route
            path="/partners"
            element={
              FEATURES.B2B_PARTNERS ? (
                <PartnersPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          {/* Public account deletion. THIS URL GOES IN PLAY CONSOLE.
              Must load without a session — the reviewer has no account. */}
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/fr/supprimer-mon-compte" element={<DeleteAccount />} />
          <Route path="/configure" element={PRELAUNCH_MODE ? <PrelaunchWaitlistPage /> : <ConfigurationWizard />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-seniors"
            element={
              FEATURES.B2B_MULTI_SENIOR ? (
                <ProtectedRoute>
                  <ManageSeniors />
                </ProtectedRoute>
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/add-senior-configuration"
            element={
              FEATURES.B2B_MULTI_SENIOR ? (
                <CaregiverPortal />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <HelpSupport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <MyAccount />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ConfigurationProvider>
    </Router>
  );
}

export default App;
